import { RequestUtils } from '@/utils/request/requestUtil'

export type JsonRecord = Record<string, unknown>
export type RequestTraceStatus = 'running' | 'done' | 'error'
export type RequestTraceCallback = (status: RequestTraceStatus, detail: string) => void
export type QueryParams = Record<string, string | number | boolean | null | undefined>

export interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PUT'
  params?: QueryParams
  body?: string
  signed?: boolean
  cacheBust?: boolean
  headers?: Record<string, string>
  traceLabel?: string
  onTrace?: RequestTraceCallback
  errorPrefix?: string
}

/**
 * 去掉 base URL 末尾斜杠。
 * @param base 原始域名或代理前缀。
 * @returns 去掉末尾 `/` 的字符串，便于后续安全拼接接口路径。
 */
export function trimSlash(base: string): string {
  return base.endsWith('/') ? base.slice(0, -1) : base
}

/**
 * 将可选查询参数序列化为 query string。
 * @param basePath 接口 path。
 * @param params 查询参数对象，空值会被跳过。
 * @returns 带 query string 的接口 path。
 */
export function withQuery(basePath: string, params: QueryParams): string {
  const query = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    query.set(key, String(value))
  }
  const qs = query.toString()
  return qs ? `${basePath}?${qs}` : basePath
}

/**
 * 按橙光接口规则对 query 参数生成签名。
 * @param params 待签名的查询参数。
 * @returns 参数排序后拼接密钥计算出的 MD5 签名。
 */
export function buildApiSign(params: QueryParams): string {
  const parts: string[] = []
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    parts.push(`${key}=${value}`)
  }
  parts.sort()
  return RequestUtils.getMd5FromString(parts.join('&') + RequestUtils.key)
}

/**
 * 从多个候选字段中挑选第一个非空文本。
 * @param record 接口返回对象。
 * @param keys 候选字段名列表，按顺序优先匹配。
 * @returns 第一个非空文本；没有可用字段时返回空字符串。
 */
export function pickText(record: JsonRecord, keys: string[]): string {
  for (const key of keys) {
    const value = String(record[key] ?? '').trim()
    if (value) return value
  }
  return ''
}

/**
 * 从接口常见包裹结构中读取数组载荷。
 * @param data 接口原始返回。
 * @returns 顶层、data、list、items、rows 等字段中的对象数组。
 */
export function getPayloadArray(data: JsonRecord): JsonRecord[] {
  const payload = (data.data as unknown) ?? data
  if (Array.isArray(payload)) return payload.filter((item): item is JsonRecord => !!item && typeof item === 'object')
  if (payload && typeof payload === 'object') {
    const record = payload as JsonRecord
    for (const key of ['list', 'items', 'rows', 'data']) {
      const value = record[key]
      if (Array.isArray(value)) return value.filter((item): item is JsonRecord => !!item && typeof item === 'object')
    }
  }
  return []
}

/**
 * 发起橙光业务 JSON 请求。
 * @param baseUrl 接口域名或开发代理前缀。
 * @param path 接口 path。
 * @param options 请求方法、参数、签名、缓存和日志配置。
 * @returns JSON 对象返回。
 * @throws HTTP 请求失败或 fetch 失败时抛出错误。
 */
export async function requestJson(
  baseUrl: string,
  path: string,
  options?: ApiRequestOptions,
): Promise<JsonRecord> {
  const method = options?.method ?? 'GET'
  const params = { ...(options?.params ?? {}) }
  if (options?.cacheBust) {
    params._t = Date.now()
  }
  if (options?.signed) {
    params.sign = buildApiSign(params)
  }
  const url = `${trimSlash(baseUrl)}${withQuery(path, params)}`
  const label = options?.traceLabel ?? `${method} ${path}`
  options?.onTrace?.('running', `${label} · start`)
  try {
    const res = await fetch(url, {
      method,
      credentials: 'include',
      cache: options?.cacheBust ? 'no-store' : undefined,
      headers: options?.headers,
      body: options?.body,
    })
    if (!res.ok) {
      throw new Error(`${options?.errorPrefix ?? '接口请求失败'}：HTTP ${res.status}`)
    }
    const data = (await res.json()) as JsonRecord
    const payload = (data.data as JsonRecord | undefined) ?? data
    const code = String(payload.ret ?? data.ret ?? payload.status ?? data.status ?? '').trim()
    options?.onTrace?.('done', `${label} · done${code ? ` · ret=${code}` : ''}`)
    return data
  } catch (error) {
    if (error instanceof Error) {
      options?.onTrace?.('error', `${label} · ${error.message}`)
    } else {
      options?.onTrace?.('error', `${label} · 请求失败`)
    }
    throw error
  }
}
